/* Build-in hooks */
import { useEffect, useState } from "react"; // Import React's useState hook

/* Supreme Edits context */
import { useSupremeEditsPlayer } from "../../contexts/SupremeEditsContext";

// Component to render waveform animation
const Waveform = ({ className }) => {
	let delays = ["0.1s", "0.3s", "0", "0.2s"];
	return (
		<div className={`waveform ${className}`}>
			<div className="bar" style={{ animationDelay: delays[0] }} />
			<div className="bar" style={{ animationDelay: delays[1] }} />
			<div className="bar" style={{ animationDelay: delays[2] }} />
			<div className="bar" style={{ animationDelay: delays[3] }} />
		</div>
	);
};

// Component to render the play icon
const PlayIcon = ({ circle, className, forceStroke }) => {
	return (
		<svg
			className={`${className} group/play-button hover:scale-110 hover:fill-white transition-all duration-500 active:scale-95 cursor-pointer fill-none`}
			width="32"
			height="32"
			viewBox="0 0 32 33"
			xmlns="http://www.w3.org/2000/svg"
		>
			<rect
				className={circle}
				x="0.5"
				y="1"
				width="31"
				height="31"
				rx="15.5"
				stroke="white"
			/>
			<g clipPath="url(#clip0_185_980)">
				<path
					className={`${forceStroke ? "stroke-black" : "stroke-white"
						} transition-colors duration-300 group-hover/playing-container:stroke-black group-hover/play-button:stroke-black`}
					d="M11.4355 21.8302C11.4391 22.0385 11.4998 22.2419 11.6108 22.4182C11.7219 22.5945 11.8792 22.737 12.0655 22.8302C12.2517 22.9353 12.4618 22.9906 12.6755 22.9906C12.8893 22.9906 13.0994 22.9353 13.2855 22.8302L21.9355 17.4802C22.1238 17.3895 22.2826 17.2476 22.3937 17.0707C22.5048 16.8938 22.5638 16.6891 22.5638 16.4802C22.5638 16.2713 22.5048 16.0666 22.3937 15.8897C22.2826 15.7128 22.1238 15.5709 21.9355 15.4802L13.2855 10.1702C13.0994 10.065 12.8893 10.0098 12.6755 10.0098C12.4618 10.0098 12.2517 10.065 12.0655 10.1702C11.8792 10.2634 11.7219 10.4059 11.6108 10.5822C11.4998 10.7585 11.4391 10.9618 11.4355 11.1702V21.8302Z"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
			<defs>
				<clipPath id="clip0_185_980">
					<rect
						width="14"
						height="14"
						fill="white"
						transform="translate(10 9.5)"
					/>
				</clipPath>
			</defs>
		</svg>
	);
};

// Main PlayButton component to handle play/pause functionality
const PlayButton = ({
	number,
	circle,
	className,
	metaData,
	remixMetadata,
	playlistTracks,
	playIndex,
	version,
	thisVersionIndex,
	thisPlaylistName,
	remixMetaFromList,
	type = "default", // Default type is 'default', can be 'edit', 'version', or 'shorter'
}) => {
	const [thisTrackId] = useState(metaData?.trackId); // Track ID for the current track
	const [thisTrack] = useState([metaData]); // Metadata for the current track
	const [matchTrack, setMatchTrack] = useState(false);

	// Access player context values and methods
	const {
		supremeCycle,
		currentIndex,
		setCurrentIndex,
		currentTrack,
		setSupremeCycle,
		isPlaying,
		setIsPlaying,
		editIndex,
		setEditIndex,
		versionIndex,
		setVersionIndex,
		playingRowType,
		setPlayingRowType,
		playlistName,
		setPlaylistName,
		trackLoading,
		playerQueue,
		setIsManualPlay,
	} = useSupremeEditsPlayer();

	const currentTrackId = currentTrack?.trackId; // Current track ID
	const currentTrackVersion =
		currentTrack?.editInfos?.[editIndex]?.versions?.[versionIndex]?.id; // Current track version type
	const thisTrackVersion = version?.id; // Version type of the track being played
	const currentTrackEditTitle =
		currentTrack?.editInfos?.[editIndex]?.editTitle; // Current track edit title
	const remixEditTitle = remixMetadata?.editTitle; // Remix metadata edit title
	//const trackAndEditMatch = (remixMetadata ? (remixMetadata && currentTrackEditTitle === remixEditTitle) : editIndex === 0 && (currentTrackId === thisTrackId));
	const isCurrentRemix = remixMetaFromList
		? remixMetaFromList?.id ===
		supremeCycle[currentIndex]?.editInfos[editIndex]?.id
		: true;

	useEffect(() => {
		const isTrackMatch =
			type !== "queue" &&
			currentTrackId === thisTrackId &&
			playlistName === thisPlaylistName &&
			(remixMetadata ? currentTrackEditTitle === remixEditTitle : true) &&
			playingRowType === type;

		const isQueueMatch =
			type === "queue" &&
			playingRowType === "queue" &&
			playlistName === thisPlaylistName &&
			supremeCycle[currentIndex]?.trackId === thisTrackId &&
			supremeCycle[currentIndex]?.queueTrackId === metaData.queueTrackId;

		const isVersionMatched =
			type === "version" && currentTrackVersion === thisTrackVersion;

		const isShorterMatch =
			type === "shorter" &&
			currentTrackId === thisTrackId &&
			playlistName === thisPlaylistName &&
			currentTrackEditTitle === remixEditTitle &&
			currentTrackVersion === thisTrackVersion;

		setMatchTrack(
			type === "version"
				? isVersionMatched
				: type === "shorter"
					? isShorterMatch
					: type === "queue"
						? isQueueMatch
						: isTrackMatch && isCurrentRemix
		);
	}, [
		currentTrack,
		currentTrackEditTitle,
		currentTrackId,
		currentTrackVersion,
		metaData,
		remixEditTitle,
		remixMetadata,
		playlistName,
		thisPlaylistName,
		thisTrackId,
		thisTrackVersion,
		type,
		version,
		editIndex,
		playingRowType,
		supremeCycle,
		currentIndex,
		setCurrentIndex,
		setSupremeCycle,
		isPlaying,
		setEditIndex,
		versionIndex,
		setVersionIndex,
		setPlayingRowType,
		setPlaylistName,
		trackLoading,
		playerQueue,
	]);

	return (
		<>
			{/* Render play/pause button for the currently playing track  */}
			{matchTrack ? (
				<div
					className={`relative group/playing-container w-8 h-8 flex items-center justify-center cursor-pointer`}
				>
					{isPlaying ? (
						<>
							<div
								onClick={() => {
									if (!trackLoading) {
										setIsPlaying(false);
									}
								}}
								className="z-10 absolute flex justify-center items-center w-8 h-8 bg-white rounded-full group-hover/playing-container:opacity-100 group-hover/playing-container:scale-105 group-active/playing-container:scale-95 transition-all duration-500 opacity-0"
							>
								<svg
									width="10"
									height="12"
									viewBox="0 0 10 12"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M1.25 1.5V10.5M8.75 1.5V10.5"
										stroke="black"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</div>
							<Waveform
								className={`z-0 absolute group-hover/playing-container:opacity-0 opacity-100 transition-opacity duration-500 delay-200`}
							/>
						</>
					) : (
						<div
							onClick={() => {
								if (!trackLoading) {
									setIsManualPlay(true); // Signal this is a manual play action
									setIsPlaying(true);
								}
							}}
							className="z-10 absolute flex justify-center items-center w-8 h-8 bg-white rounded-full transition-all duration-500"
						>
							<PlayIcon
								circle={circle}
								forceStroke={true}
								className={`fill-white transition-all duration-500 cursor-pointer`}
							/>
						</div>
					)}
				</div>
			) : number ? (
				// Render track number for non-playing tracks
				<div className="group w-8 h-8 relative cursor-pointer">
					<p className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 group-hover:opacity-0 delay-200 transition-all duration-300 opacity-100 track-number text-transparent text-[30px] sm:text-[36px] md:text-[40px] leading-8 font-black">
						{number}
					</p>
					{/* Play button for non-playing tracks */}
					<div
						onClick={() => {
							setIsManualPlay(true); // Signal this is a manual play action
							setPlaylistName(thisPlaylistName);
							setPlayingRowType(type);

							if (type === "queue") {
								// For queue tracks, set the supreme cycle to player queue and current index to the clicked track
								setSupremeCycle(playerQueue);
								setCurrentIndex(playIndex);
								setEditIndex(0);
								setVersionIndex(0);
							} else if (type === "version") {
								// For version play, set supremeCycle to playlistTracks or thisTrack, set all indices
								setSupremeCycle(playlistTracks || thisTrack);
								setCurrentIndex(0);
								// setEditIndex(0);
								setVersionIndex(thisVersionIndex);
								setIsPlaying(true);
								return;
							} else {
								setSupremeCycle(playlistTracks || thisTrack);
								setCurrentIndex(0);
								setEditIndex(0);
								setVersionIndex(thisVersionIndex);
							}
						}}
					>
						<PlayIcon
							circle={circle}
							className={`group-hover:scale-105 group-hover:fill-white group-hover:opacity-100 opacity-0 transition-all duration-500 cursor-pointer fill-none w-[24px] h-[24px] sm:w-[30px] sm:h-[30px]`}
						/>
					</div>
				</div>
			) : (
				// Default play button for tracks without a number
				<div
					onClick={() => {
						setIsManualPlay(true); // Signal this is a manual play action
						setPlaylistName(thisPlaylistName);
						setPlayingRowType(type);

						if (thisPlaylistName === "for-you") {
							setSupremeCycle(playlistTracks || thisTrack);
							setCurrentIndex(0);
							setEditIndex(0);
							setVersionIndex(thisVersionIndex);
							setIsPlaying(true);
							return;
						}

						if (type === "queue") {
							// For queue tracks, set the supreme cycle to player queue and current index to the clicked track
							setSupremeCycle(playerQueue);
							setCurrentIndex(playIndex);
							setEditIndex(0);
							setVersionIndex(0);
							setIsPlaying(true);
							return;
						}

						if (type === "version") {
							setSupremeCycle(playlistTracks || thisTrack);
							setCurrentIndex(0);
							// setEditIndex(0);
							setVersionIndex(thisVersionIndex);
							setIsPlaying(true);
							return;
						}

						if (version) {
							setVersionIndex(thisVersionIndex);
							setIsPlaying(true);
						} else if (remixMetadata) {
							setSupremeCycle(playlistTracks || thisTrack);
							setCurrentIndex(0);
							setEditIndex(playIndex + 1);
							setIsPlaying(true);
						} else {
							setSupremeCycle(playlistTracks || thisTrack);
							setCurrentIndex(playIndex ? playIndex : 0);
							setEditIndex(0);
							setIsPlaying(true);
						}
					}}
				>
					<PlayIcon circle={circle} className={className} />
				</div>
			)}
		</>
	);
};

export default PlayButton;
